/**
 * Sticky Add-to-Cart Mobile Banner
 * Provides a mobile-first sticky banner for product pages with size selection and add-to-cart functionality
 * Only displays on mobile devices (< 1000px) when the main product form is not visible
 */

import { isElementPartiallyVisible } from './dom-utils.js';
import { checkProductQuantityLimit } from './product-quantity-limit.js';

window.BSS = window.BSS || {};
BSS.Components = BSS.Components || {};

BSS.Components.StickyATCBanner = (function () {
  'use strict';

  // Configuration object containing all selectors and settings
  const CONFIG = {
    headerSelector: '#shopify-section-header',
    addToCartFormSelector: '#AddToCartForm',
    productTitleSelector: '.product__title, h1',
    productPriceSelector: '.product-header__price span#product-price',
    addToCartButtonSelector: '[name="add"]',
    sizeSelector: '[data-swatch-type="size"] input:checked',
    sizeOptionsSelector: '[data-swatch-type="size"] .swatch-element',
    sizeInputsSelector: '[data-swatch-type="size"] input',
    waitlistContainerSelector: '.waitlist-pdp-container',
    mobileBreakpoint: 1000,

    // CSS Classes
    classes: {
      visible: 'visible',
      hidden: 'hidden',
      soldout: 'soldout'
    },

    // Timing constants
    timing: {
      sizeValidationHighlight: 2000,
      bannerContentUpdate: 100,
      priceObserverDelay: 50,
      loadingSpinnerDuration: 2000
    },

    // Banner element selectors
    banner: {
      id: 'sticky-atc-banner',
      container: '.sticky-atc-container',
      title: '.sticky-atc-title',
      price: '.sticky-atc-price',
      size: '.sticky-atc-size',
      dropdown: '.sticky-size-select',
      dropdownContainer: '.sticky-atc-size-dropdown',
      button: '.sticky-atc-button'
    }
  };

  /**
   * Creates the sticky banner HTML structure
   * @returns {HTMLElement} The complete sticky banner element
   */
  function createStickyBanner() {
    const banner = document.createElement('div');
    banner.id = CONFIG.banner.id;
    banner.innerHTML = `
        <div class="sticky-atc-container">
          <div class="sticky-atc-product-info">
            <div class="sticky-atc-details">
              <h3 class="sticky-atc-title"></h3>
              <div class="sticky-atc-price"></div>
            </div>
          </div>
          <div class="sticky-atc-actions">
            <div class="sticky-atc-size-dropdown">
              <select id="stickyATCSizeSelector" name="Sticky Add to Cart Size Selector" class="sticky-size-select">
              <option value="">Select Size</option>
              </select>
            </div>
            <button class="sticky-atc-button" type="button">
              ADD TO BAG
            </button>
          </div>
        </div>
      `;

    return banner;
  }

  /**
   * Extracts available size options from the product form
   * @returns {Array<Object>} Array of size objects with value, label, element, and isChecked properties
   */
  function getAvailableSizes() {
    const sizeElements = document.querySelectorAll(CONFIG.sizeOptionsSelector);
    const sizes = [];

    sizeElements.forEach(element => {
      // Skip hidden size items (variants that have different size options)
      if (element.style.display === 'none') return;

      // Find the offscreen input within this element
      const input = element.querySelector('input.offscreen');
      if (input && input.value) {
        sizes.push({
          value: input.value,
          label: input.value,
          element: input,
          isChecked: input.checked
        });
      }
    });

    return sizes;
  }

  // Get the size that has the checked attribute
  /**
   * Gets the currently selected size from the product form
   * @returns {string|null} The value of the checked size option, or null if none selected
   */
  function getCheckedSize() {
    const sizeElements = document.querySelectorAll(CONFIG.sizeOptionsSelector);

    for (const element of sizeElements) {
      const input = element.querySelector('input.offscreen');
      if (input && input.value && input.checked) {
        return input.value;
      }
    }

    return null;
  }

  /**
   * Determines if the size dropdown should be hidden for one-size products
   * @param {Array<Object>} availableSizes - Array of available size options
   * @returns {boolean} True if dropdown should be hidden (one-size product)
   */
  function shouldHideSizeDropdown(availableSizes) {
    // If there's not exactly one size, show the dropdown
    if (availableSizes.length !== 1) {
      return false;
    }

    // Get the single size value and normalize it for comparison
    const sizeValue = availableSizes[0].value
      .toLowerCase()
      .replace(/[\s-_]/g, '');

    // Check if it matches "onesize" in any variation
    return sizeValue === 'onesize';
  }

  /**
   * Updates the size dropdown with available options and pre-selects the current size
   * @param {HTMLElement} banner - The sticky banner element
   */
  function updateBannerSizeDropdown(banner) {
    const dropdown = banner.querySelector(CONFIG.banner.dropdown);
    const dropdownContainer = banner.querySelector('.sticky-atc-size-dropdown');
    if (!dropdown || !dropdownContainer) return;

    const availableSizes = getAvailableSizes();
    const checkedSize = getCheckedSize();

    /* One-Size Logic */
    if (shouldHideSizeDropdown(availableSizes)) {
      dropdownContainer.classList.add(CONFIG.classes.hidden);
      return; // Exit early, no need to populate dropdown options
    } else {
      dropdownContainer.classList.remove(CONFIG.classes.hidden);
    }

    // Clear existing options except the first one
    dropdown.innerHTML = '<option value="">Select Size</option>';

    // Add available sizes
    availableSizes.forEach(size => {
      const option = document.createElement('option');
      option.value = size.value;
      option.textContent = size.label;
      dropdown.appendChild(option);
    });

    // Pre-select the checked size if it exists
    if (checkedSize) {
      dropdown.value = checkedSize;
    }

    // Disable dropdown if no sizes available
    dropdown.disabled = availableSizes.length === 0;
  }

  /**
   * Updates the sticky banner when called externally (e.g., from variant change callback)
   */
  function updateStickyBannerFromVariantChange() {
    const banner = document.getElementById('sticky-atc-banner');
    if (!banner) return;

    updateBannerSizeDropdown(banner);
    updateBannerButton(banner);

    const productInfo = getProductInfo();
    updateBannerContent(banner, productInfo);
  }

  /**
   * Handles size dropdown change events from the sticky banner and syncs selection to the product form
   * @param {Event} event - The change event from the sticky banner size dropdown
   */
  function handleBannerSizeChange(event) {
    const selectedValue = event.target.value;
    if (!selectedValue) return;

    // Find and select the corresponding size option in the original form
    const sizeElements = document.querySelectorAll(CONFIG.sizeOptionsSelector);

    sizeElements.forEach(element => {
      const input = element.querySelector('input.offscreen');
      if (input && input.value === selectedValue) {
        // Uncheck all other radios in this group first
        const radioName = input.name;
        document
          .querySelectorAll(`input[name="${radioName}"]`)
          .forEach(radio => {
            radio.checked = false;
          });

        // Set this radio as checked
        input.checked = true;

        // Trigger both change and click events to ensure all handlers fire
        input.dispatchEvent(new Event('change', { bubbles: true }));
        input.dispatchEvent(new Event('click', { bubbles: true }));

        // Also click the label if it exists for UI feedback
        const label = element.querySelector('label');
        if (label) {
          label.click();
        }
      }
    });

    // Update the banner content after a short delay
    setTimeout(() => {
      const banner = document.getElementById('sticky-atc-banner');
      if (banner) {
        const productInfo = getProductInfo();
        updateBannerContent(banner, productInfo);
        updateBannerButton(banner);
      }
    }, CONFIG.timing.bannerContentUpdate);
  }

  /**
   * Extracts current product information from the page
   * @returns {Object} Product info object with title, price, and size properties
   */
  function getProductInfo() {
    const productTitle = document.querySelector(CONFIG.productTitleSelector);
    const productPrice = document.querySelector(CONFIG.productPriceSelector);
    const selectedSize = document.querySelector(CONFIG.sizeSelector);

    return {
      title: productTitle ? productTitle.textContent.trim() : '',
      price: productPrice ? productPrice.textContent.trim() : '',
      size: selectedSize
        ? selectedSize.value || selectedSize.getAttribute('data-value')
        : ''
    };
  }

  /**
   * Updates the banner content with current product information
   * @param {HTMLElement} banner - The sticky banner element
   * @param {Object} productInfo - Product information object
   */
  function updateBannerContent(banner, productInfo) {
    const title = banner.querySelector(CONFIG.banner.title);
    const price = banner.querySelector(CONFIG.banner.price);
    const dropdown = banner.querySelector(CONFIG.banner.dropdown);

    if (title) title.textContent = productInfo.title;
    if (price) price.textContent = productInfo.price;

    // Update dropdown selection
    if (dropdown && productInfo.size) {
      dropdown.value = productInfo.size;
    }
  }

  /**
   * Updates the banner's add-to-cart button state based on original button availability
   * @param {HTMLElement} banner - The sticky banner element
   */
  function updateBannerButton(banner) {
    const button = banner.querySelector(CONFIG.banner.button);
    const waitlistContainer = document.querySelector(
      CONFIG.waitlistContainerSelector
    );

    if (!button) return;

    // Check if variant is out of stock (waitlist container is visible)
    const isOutOfStock =
      waitlistContainer &&
      window.getComputedStyle(waitlistContainer).display !== 'none';

    if (isOutOfStock) {
      button.onclick = function (event) {
        event.preventDefault();
        document
          .querySelector(CONFIG.addToCartFormSelector)
          .scrollIntoView({ behavior: 'smooth', block: 'center' });
      };
      button.textContent = 'JOIN WAITLIST';
      button.style.background = '#141e30';
    } else {
      button.disabled = false;
      button.textContent = 'ADD TO BAG';
      button.style.background = '';
      button.onclick = null;
    }

    // Check quantity limit
    checkProductQuantityLimit();
  }

  /**
   * Controls banner visibility based on product form visibility and scroll position
   * Banner shows when form is not visible in viewport
   */
  function handleBannerScroll() {
    const banner = document.getElementById(CONFIG.banner.id);
    const addToCartForm = document.querySelector(CONFIG.addToCartFormSelector);

    if (!banner || !addToCartForm) return;

    // Check if the AddToCartForm is visible in the viewport
    const isFormVisible =
      isElementPartiallyVisible(addToCartForm) || window.scrollY == 0;

    if (!isFormVisible) {
      banner.classList.add(CONFIG.classes.visible);
    } else {
      banner.classList.remove(CONFIG.classes.visible);
    }
  }

  /**
   * Handles banner add-to-cart button clicks with size validation and loading states
   * Ensures the product form has the correct variant selected before submission
   */
  function handleBannerAddToCart(event) {
    const banner = document.getElementById(CONFIG.banner.id);
    const dropdown = banner?.querySelector(CONFIG.banner.dropdown);
    const dropdownContainer = banner?.querySelector(
      CONFIG.banner.dropdownContainer
    );
    const stickyATCButton = banner?.querySelector(CONFIG.banner.button);
    const productFormATCButton = document.querySelector(
      CONFIG.addToCartButtonSelector
    );
    const productForm = document.querySelector(CONFIG.addToCartFormSelector);
    const waitlistContainer = document.querySelector(
      CONFIG.waitlistContainerSelector
    );

    // Check if button is in waitlist mode
    const isWaitlistMode =
      waitlistContainer &&
      window.getComputedStyle(waitlistContainer).display !== 'none';

    // If in waitlist mode, the onclick handler in updateBannerButton will handle the scroll
    // So we just return early and let that handler do its job
    if (isWaitlistMode) {
      return;
    }

    const availableSizes = getAvailableSizes();
    const hasAvailableSizes = availableSizes.length > 0;
    const isDropdownHidden =
      dropdownContainer &&
      dropdownContainer.classList.contains(CONFIG.classes.hidden);

    /* One-Size Logic */
    if (
      hasAvailableSizes &&
      !isDropdownHidden &&
      (!dropdown || !dropdown.value)
    ) {
      // Highlight the dropdown to indicate selection is needed
      if (dropdown) {
        dropdown.style.borderColor = '#dc2626';
        dropdown.focus();
        setTimeout(() => {
          dropdown.style.borderColor = '#d1d5db';
        }, CONFIG.timing.sizeValidationHighlight);
      }
      return;
    }

    // Verify that a variant is selected in the product form
    const productSelect = productForm?.querySelector('#productSelect');
    if (!productSelect || !productSelect.value) {
      console.error('No variant selected in product form');
      return;
    }

    // Check if the form or button is disabled
    if (productFormATCButton?.disabled) {
      return;
    }

    // Show loading spinner on sticky button
    if (stickyATCButton) {
      const originalHTML = stickyATCButton.innerHTML;
      stickyATCButton.innerHTML =
        '<i class="cart-loading fa fa-spin fa-lg fa-spinner"></i>';

      // Reset button after 2 seconds
      setTimeout(() => {
        stickyATCButton.innerHTML = originalHTML;
      }, CONFIG.timing.loadingSpinnerDuration);
    }

    // Click the original add-to-cart button
    if (productFormATCButton) {
      productFormATCButton.click();
    }
  }

  /**
   * Handles window resize events by re-checking banner visibility
   */
  function handleBannerResize() {
    handleBannerScroll(); // Re-check visibility on resize
  }

  /**
   * Sets up event listeners for ProductForm changes and syncs them to the sticky banner
   * Handles two-way synchronization from product form → sticky banner
   * @param {HTMLElement} banner - The sticky banner element
   */
  function handleProductFormSizeChange(banner) {
    // Watch for any changes to size inputs on the product form
    const sizeInputs = document.querySelectorAll(CONFIG.sizeInputsSelector);

    sizeInputs.forEach(input => {
      // Listen for both change and click events to catch all radio button interactions
      const handleSizeInputChange = e => {
        setTimeout(() => {
          // Update sticky banner when product page changes
          const updatedInfo = getProductInfo();
          updateBannerContent(banner, updatedInfo);
          updateBannerButton(banner);
          updateBannerSizeDropdown(banner);

          // Sync size dropdown value
          const sizeDropdown = banner.querySelector(CONFIG.banner.dropdown);
          const checkedSize = getCheckedSize();
          if (
            sizeDropdown &&
            checkedSize &&
            sizeDropdown.value !== checkedSize
          ) {
            sizeDropdown.value = checkedSize;
          }
        }, CONFIG.timing.bannerContentUpdate);
      };

      input.addEventListener('change', handleSizeInputChange);
      input.addEventListener('click', handleSizeInputChange);
    });

    // Also use event delegation on the swatch container for more reliable radio button detection
    const swatchContainer = document.querySelector('[data-swatch-type="size"]');
    if (swatchContainer) {
      swatchContainer.addEventListener('change', e => {
        if (e.target.matches('input[type="radio"]')) {
          setTimeout(() => {
            const updatedInfo = getProductInfo();
            updateBannerContent(banner, updatedInfo);
            updateBannerButton(banner);
            updateBannerSizeDropdown(banner);

            // Sync size dropdown value
            const sizeDropdown = banner.querySelector(CONFIG.banner.dropdown);
            const checkedSize = getCheckedSize();
            if (
              sizeDropdown &&
              checkedSize &&
              sizeDropdown.value !== checkedSize
            ) {
              sizeDropdown.value = checkedSize;
            }
          }, CONFIG.timing.bannerContentUpdate);
        }
      });
    }

    // Watch for price changes (indicates variant change)
    const priceElement = document.querySelector(CONFIG.productPriceSelector);
    if (priceElement) {
      const observer = new MutationObserver(() => {
        setTimeout(() => {
          const updatedInfo = getProductInfo();
          updateBannerContent(banner, updatedInfo);
          updateBannerButton(banner);
          updateBannerSizeDropdown(banner);
        }, CONFIG.timing.priceObserverDelay);
      });

      observer.observe(priceElement, {
        childList: true,
        subtree: true,
        characterData: true
      });
    }
  }

  /**
   * Initializes the sticky banner and sets up all event listeners
   * Called when DOM is ready
   */
  function init() {
    // Create and insert banner
    const banner = createStickyBanner();
    document.body.appendChild(banner);

    // Update size dropdown and pre-select checked size
    updateBannerSizeDropdown(banner);

    // Update content
    const productInfo = getProductInfo();
    updateBannerContent(banner, productInfo);

    // Update button state
    updateBannerButton(banner);

    // Add event listeners
    const stickyButton = banner.querySelector(CONFIG.banner.button);
    const sizeDropdown = banner.querySelector(CONFIG.banner.dropdown);

    if (stickyButton) {
      stickyButton.addEventListener('click', handleBannerAddToCart);
    }

    if (sizeDropdown) {
      sizeDropdown.addEventListener('change', handleBannerSizeChange);
    }

    // Handle scroll and resize
    window.addEventListener('scroll', handleBannerScroll);
    window.addEventListener('resize', handleBannerResize);

    // Set up ProductForm → Banner synchronization
    handleProductFormSizeChange(banner);

    // Watch for price changes (in case of variant pricing)
    const priceElement = document.querySelector(CONFIG.productPriceSelector);
    if (priceElement) {
      const observer = new MutationObserver(() => {
        const updatedInfo = getProductInfo();
        updateBannerContent(banner, updatedInfo);
      });

      observer.observe(priceElement, {
        childList: true,
        subtree: true,
        characterData: true
      });
    }

    handleBannerScroll();
  }

  return {
    init,
    updateStickyBannerFromVariantChange
  };
})();
